import { IconButton, Input } from "@design";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { ListNav } from "@/components/ListNav";
import { Page } from "@/components/Page";
import { Text } from "@/components/Text";
import { appVersion, versionLine } from "@/features/about/appVersion";
import { useViewport } from "@/responsive/viewport";
import { HelpList, HelpRow } from "./HelpRow";
import { HelpSearchResults } from "./HelpSearchResults";
import { GROUP_ICON, useHelpNav } from "./helpNav";
import { useHelpArticles } from "./useHelpArticles";

/** Which version this is, quietly at the bottom (the account menu's About has the details). */
function HelpVersionLine() {
  const { t } = useTranslation("shell", { keyPrefix: "about" });
  return (
    <Text variant="caption" tone="tertiary" className="mt-10">
      {t("helpVersion", { version: versionLine(appVersion()) })}
    </Text>
  );
}

/** /help: search, the "Start here" path for new users, common questions, and (on phones) every topic. */
export function HelpLandingPage() {
  const { t } = useTranslation("help");
  const { isMobile } = useViewport();
  const articles = useHelpArticles();
  const nav = useHelpNav(articles);
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const setQuery = (q: string) => setParams(q ? { q } : {}, { replace: true });

  return (
    <Page title={t("title")} subtitle={t("subtitle")}>
      <search className="w-full max-w-2xl">
        <Input
          type="search"
          aria-label={t("search.label")}
          placeholder={t("search.placeholder")}
          leadingIcon={<Search />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          trailingAction={
            query ? (
              <IconButton
                icon={<X />}
                aria-label={t("search.clear")}
                variant="ghost"
                size="sm"
                onClick={() => setQuery("")}
              />
            ) : undefined
          }
        />
      </search>

      {query.trim() ? (
        <div className="max-w-2xl">
          <HelpSearchResults articles={articles} query={query.trim()} />
        </div>
      ) : (
        <div className="flex max-w-2xl flex-col gap-8">
          <HelpList title={t("landing.startHere")} hint={t("landing.startHereHint")}>
            {articles
              .filter((a) => a.startHere)
              .map((article, i) => (
                <HelpRow
                  key={article.slug}
                  to={`/help/${article.slug}`}
                  title={article.title}
                  detail={article.summary}
                  icon={GROUP_ICON.start}
                  brand={i === 0}
                />
              ))}
          </HelpList>
          <HelpList title={t("landing.commonQuestions")}>
            {articles
              .filter((a) => a.group === "faq")
              .map((article) => (
                <HelpRow
                  key={article.slug}
                  to={`/help/${article.slug}`}
                  title={article.title}
                  detail={article.summary}
                  icon={GROUP_ICON.faq}
                />
              ))}
          </HelpList>
          {isMobile && <ListNav groups={nav.slice(1)} aria-label={t("landing.browse")} variant="list" />}
        </div>
      )}
      <HelpVersionLine />
    </Page>
  );
}
